import { useEffect, useState, type ReactElement } from 'react';
import { toast } from '../../../design-system/components';
import { useSession } from '../../auth';
import { navigate, sanitizeReturnTo } from '../router';
import { PageBack } from '../components/PageBack';
import { PageHome } from '../components/PageHome';
import { AccountClosedScreen } from '../components/AccountClosedScreen';
import { CallGlyph } from '../components/CallGlyph';
import { track } from '../../../../../shared-web/track';
import {
  openInboundDoor, useInboundDoor, useVisitorConsent, inboundDoorFailureMessage,
} from '../../../../../shared-web/inbound-door';
import { isInAppWebview } from '../../../../../shared-web/ua-detect';
import { GOOGLE_CLIENT_ID, googleSignInHref } from '../../../../../shared-web/google-signin';
import { GoogleG } from '../../../../../shared-web/google-button';
import { ConsentTerms, CONSENT_AGE } from '../components/ConsentTerms';
import './Auth.css';

/**
 * Which entrance sent them here, and the one sentence that entrance earns.
 *
 * The marketing bundle's dialog (removed 2026-09-06) used to dress itself by
 * entrance; this page was one page for all of them, so every link that used to
 * open the dialog with "Keep your story" had to keep using the old `?signin=1`
 * address to keep its words. Now `/auth?arrival=trial` says the same thing, and
 * there is one address for signing in (2026-08-31).
 *
 * Cosmetic only: the `login.*` beacons from this page stay `arrival: 'auth'` —
 * the page IS the entrance as far as the funnel is concerned, and each of these
 * links already has its own click event upstream.
 */
const ARRIVAL_COPY: Record<string, { title: string; note: string }> = {
  // The /collect phone-call doors (mic blocked, call too short, trial spent).
  trial: {
    title: 'Keep your Ember with you',
    note: 'What you tell Ember comes with you — one text is the whole account.',
  },
  dashboard: {
    title: 'Verify it’s you',
    note: 'One text and your notebook opens.',
  },
  // `submit` / `gift` arrivals were removed 2026-09-09: nothing links them.
};

export interface AuthPageProps {
  search: URLSearchParams;
}

/**
 * The sign-in page — one door, the same one that registers (2026-08-31 拍板).
 *
 * It used to be two: a phone number and a 6-digit code WE texted (Firebase OTP)
 * above, and "New to Ember? Join by text" below — two mechanisms for one act,
 * on one screen, with the OTP half asking for a number, a code, a wait for the
 * SMS, and a picker of country dial codes, plus a fallback door for every
 * region and network our codes cannot reach. The other half needs none of
 * that: the visitor sends ONE prefilled text, and the handle it arrives from
 * IS the account — registering and signing in have always been the same act,
 * so the page now has one way to do it. (The /api/auth/code/* OTP endpoints
 * were removed on 2026-09-06; the one door has no second mechanism.)
 *
 * What is left on the page: what the account is for, the two consents a first
 * visit owes — and only a first visit, because a browser that has already
 * ticked them has said the thing and being asked again is a step, not a
 * consent (visitor-consent.ts) — and one button. Tapping it leases a line and
 * a code and hands the composer the draft; on a phone that is a jump straight
 * into Messages.
 *
 * The waiting belongs to the app, not to this page (inbound-wait.ts): the
 * person is in Messages when their text lands, and they may come back here, to
 * the homepage, or to a reloaded tab. Whichever it is, the overlay is up, still
 * listening, and the page reloads itself signed in.
 */
export function AuthPage({ search }: AuthPageProps): ReactElement {
  const returnTo = sanitizeReturnTo(search.get('returnTo'), '/');
  const arrivalCopy = ARRIVAL_COPY[String(search.get('arrival') || '').trim()] || null;
  const closedAtDoor = String(search.get('account') || '').trim() === 'closed';
  const { status } = useSession();
  const recorded = useVisitorConsent();
  const { arming } = useInboundDoor();

  const [ageOk, setAgeOk] = useState(false);
  const [tosOk, setTosOk] = useState(false);
  const [consentAttention, setConsentAttention] = useState(false);
  /** The server refused the record this page trusted — ask, whatever the read
   *  said. The one way the two can disagree is a Terms bump between the read
   *  and the tap, which is exactly when the boxes have to come back. */
  const [mustAsk, setMustAsk] = useState(false);

  /**
   * Are the boxes this page's business at all?
   *
   * Only for a browser with no acceptance on file, or one whose acceptance
   * predates the current Terms — the server decides which (GET
   * /api/visitor/consent compares against CURRENT_LEGAL_VERSION). While the
   * read is still out, the boxes stand: not knowing means asking.
   */
  const asksConsent = mustAsk || !(recorded.known && recorded.consented);

  // 授权码流的失败落点(?google=state|rejected|failed|unavailable):说一句
  // 人话。取消不来这里(callback 直接送回出发页)。
  useEffect(() => {
    const g = String(search.get('google') || '').trim();
    if (!g) return;
    toast.error(g === 'unavailable'
      ? 'Google sign-in isn’t available right now — continue by text instead.'
      : 'Google sign-in didn’t complete. Try again.');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Already signed in → straight through. /collect is a server-rendered page,
  // not an SPA route — SPA-navigating there would render the 404 shell, so it
  // takes a full load (same as the post-sign-in hop the wait overlay makes).
  useEffect(() => {
    if (status !== 'authenticated' || closedAtDoor) return;
    if (returnTo.startsWith('/collect')) {
      window.location.replace(returnTo);
      return;
    }
    navigate(returnTo, { replace: true });
  }, [status, returnTo, closedAtDoor]);

  /** A tap that cannot proceed must SAY so rather than sit under a dead
   *  button: the consent block flashes for attention. */
  const consentReady = (): boolean => {
    if (!asksConsent || (ageOk && tosOk)) return true;
    track('login.failed', { arrival: 'auth', step: 'handoff', reason: 'consent_missing' });
    setConsentAttention(false);
    window.requestAnimationFrame(() => {
      setConsentAttention(true);
      window.setTimeout(() => setConsentAttention(false), 760);
    });
    return false;
  };

  /**
   * The page's one action: prepare the message and hand it to Messages.
   *
   * The consent stamp goes with it only when the boxes were actually on screen
   * — otherwise the server reads this browser's record and keeps the original
   * moment of acceptance, which is the moment that means anything.
   */
  const continueByText = async (): Promise<void> => {
    if (arming) return;
    if (!consentReady()) return;
    track('login.handoff_requested', { arrival: 'auth' });
    const result = await openInboundDoor({
      arrival: 'auth',
      // The plain onboarding completion, as every SPA door uses — see
      // docs/login-and-registration-doors.md for why the marketing dialog's
      // 'collect' still differs.
      surface: 'me',
      returnTo,
      tosAcceptedAt: asksConsent ? new Date().toISOString() : '',
    });
    if (!result.ok) {
      // Not a failure: this browser is signed in and the page has not caught
      // up yet. The door has already asked the session to re-read itself; the
      // redirect effect above takes it from there. No toast, no beacon.
      if (result.code === 'already_signed_in') return;
      track('login.failed', { arrival: 'auth', step: 'handoff', reason: result.code });
      // The record stopped answering mid-visit (a Terms bump between the read
      // and this tap): draw the boxes rather than leave a refusal with no way
      // to satisfy it.
      if (result.code === 'tos_acceptance_required' || result.code === 'age_confirmation_required') {
        setMustAsk(true);
      }
      toast.error(inboundDoorFailureMessage(result.code));
    }
  };

  // ── The Google door(最下边的那颗,2026-09-09)——授权码重定向流:整页去
  //    /api/auth/google/start,服务端换码建号种会话后跳回 returnTo(弃 GIS
  //    iframe:弹窗流在第三方 Cookie 被禁的浏览器里死在空白 /gsi/transform)。
  //    内置浏览器里没有这扇门:Google 官方封禁 webview 里的 OAuth
  //    (disallowed_useragent),摆一颗必败的按钮不如不摆。 ──
  const googleDoor = Boolean(GOOGLE_CLIENT_ID) && !isInAppWebview();
  const [googleLeaving, setGoogleLeaving] = useState(false);

  const goGoogle = async (): Promise<void> => {
    if (googleLeaving) return;
    if (!consentReady()) return;
    setGoogleLeaving(true);
    track('login.google_requested', { arrival: 'auth' });
    // 这个浏览器的同意记录:与文字门写的是同一张 visitor 记录,只在勾选框
    // 真的画在屏幕上时才补写;账号级的接受由服务端在建号时落。
    if (asksConsent) {
      await fetch('/api/visitor/consent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ age_confirmed: true, tos_accepted: true, door: 'register' }),
      }).catch(() => {});
    }
    window.location.assign(googleSignInHref(returnTo));
  };

  // A sign-in door answered account_closed (2026-10-21 Riley): the proof held,
  // the account is closed — the text door's poll, the Google redirect and an
  // identified link all land here as /auth?account=closed. No form, no way to
  // register: the same phone or Google is that closed account.
  if (closedAtDoor) return <AccountClosedScreen where="sign_in" />;

  if (status === 'loading' || status === 'authenticated') {
    return <div className="ember-container auth-page" aria-busy={status === 'loading'} />;
  }

  return (
    <div className="ember-container auth-page">
      <div className="auth-toolbar">
        <PageBack className="auth-back" />
        {/* Sign-in is often several hops deep (footer → narrative page →
            Sign in); Back only retraces them, so the flow page carries the
            one-tap exit too. */}
        <div className="ember-toolbar-actions">
          <PageHome />
        </div>
      </div>
      <form
        className="auth-card"
        onSubmit={(e) => { e.preventDefault(); void continueByText(); }}
      >
        {/* Neutral like the button (2026-08-31): first-timers land here too —
            the person icon and footer send everyone through this one door, and
            "Welcome back" read to them as the wrong room. */}
        <h1 className="auth-title">{arrivalCopy?.title || 'Continue with a text'}</h1>
        {arrivalCopy ? <p className="auth-arrival-note">{arrivalCopy.note}</p> : null}
        {/* Why the phone number: the two things an account actually unlocks.
            Informational rows, deliberately not card-shaped — nothing here is
            clickable, unlike the chooser cards this icon language comes from. */}
        <ul className="auth-perks">
          <li>
            <span className="auth-perk-icon" aria-hidden>
              <CallGlyph size={16} />
            </span>
            Your phone number is your account &mdash; new or returning
          </li>
          <li>
            <span className="auth-perk-icon" aria-hidden>
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
              </svg>
            </span>
            Every Ember you tell stays saved to you
          </li>
        </ul>

        {/* Asked once per browser, never again — see visitor-consent.ts. */}
        {asksConsent && (
          <div className={`auth-consents${consentAttention ? ' needs-attention' : ''}`}>
            <label className="auth-check">
              <input type="checkbox" checked={ageOk} disabled={arming} data-log="login_age_confirm" onChange={(e) => setAgeOk(e.target.checked)} />
              <span>{CONSENT_AGE}</span>
            </label>
            <label className="auth-check">
              <input type="checkbox" checked={tosOk} disabled={arming} data-log="login_tos_agree" onChange={(e) => setTosOk(e.target.checked)} />
              <ConsentTerms door="register" />
            </label>
          </div>
        )}

        <button
          type="submit"
          className="ember-btn ember-btn-primary auth-cta"
          data-log="auth_register_inbound"
          disabled={arming}
        >
          {/* Neutral on purpose: at this point nobody knows whether this
              number has an account, and the sentence must hold either way —
              who they are decides the reply, not the button (2026-08-31). */}
          {arming ? 'Preparing…' : 'Continue by text'}
        </button>

        {/* "Send it, then come back" is said by the wait overlay the moment the
            composer opens (2026-09-06 走查: three paragraphs to read before a
            first tap; the sentence matters after the tap, and that is where it
            lives now — InboundWaitOverlay). */}

        {/* The Google door (2026-09-09):自绘按钮(官方四色 G),点击先过
            consentReady(未勾则闪勾选框),再整页出发去授权码流。 */}
        {googleDoor && (
          <div className="auth-alt-door">
            <div className="auth-or" aria-hidden><span />or<span /></div>
            <button
              type="button"
              className="ember-btn ember-btn-secondary auth-google-btn"
              data-log="auth_google"
              disabled={googleLeaving}
              onClick={() => { void goGoogle(); }}
            >
              <GoogleG size={18} />
              <span>{googleLeaving ? 'Opening Google…' : 'Continue with Google'}</span>
            </button>
          </div>
        )}
      </form>

      <section className="auth-register-section">
        {/* One more thing, once (2026-09-05 walkthrough: the page said the
            same sentence three times). Registration only ASSIGNS the picture
            (2026-09-01 拍板) — no picker here; the notebook header is where
            it becomes theirs. */}
        <p className="auth-register-copy">
          New accounts start with a small picture &mdash; an animal in a quiet
          place, picked for you. You can change it any time.
        </p>
      </section>
    </div>
  );
}
